<script setup lang="ts">
import AdvantageList from './index/_components/advantage/AdvantageList.vue';
import HotelBlocking from './index/_components/HotelBlocking.vue';
import UtmCampaignModal from './index/_components/UtmCampaignModal.vue';
import BusyWay from './index/_components/BusyWay.vue';
import VehicleLinks from './index/_components/VehicleLinks.vue';
import DownloadApp from './index/_components/download-app/DownloadApp.vue';
import CityCards from './index/_components/CityCards.vue';
import LandingBanners from '@components/ui/index&flight-info/LandingBanners.vue';
import SearchForm from '@components/ui/index&view&widget/SearchForm.vue';
import TopBanners from '@components/ui/index&view/TopBanners.vue';
import { type UseModalResult } from '@composable/useModal';
import { ReserveType } from '@utils/itinerary';
import { SearchMode } from '@utils/types';

const searchFormStore = useSearchFormStore();
const authStore = useAuthStore();
const contentStore = useContentStore();
const {
    utmCampaigns,
    landingPageTitle,
    landingPageDescription,
    landingCityName,
    landingPageContent,
} = classToRefs(contentStore);
const route = useRoute();
const { isAirplaneTicket } = useWhiteLabel();
const { isWebApp } = useDevice();
onServerPrefetch(() => {
    if (isAirplaneTicket.value) {
        return showError({ statusCode: 404 });
    }
});
await useAsyncData('data', nuxtApp => {
    delete nuxtApp?.payload?.path;
    return Promise.resolve(true);
});
useServerHead({
    htmlAttrs: {
        class: 'smooth-scroll',
    },
});

await useAsyncData(
    async () => {
        try {
            await Promise.all([
                contentStore.getPageData(route.path),
                contentStore.loadHomepageMessages(),
                contentStore.loadTopBanners(),
            ]);
        } catch (e) {
            reportException(e);
        }
        return true;
    },
    { watch: [() => route.path] }
);

const title = computed(
    () =>
        landingPageTitle.value ||
        'مِستربلیط سامانه رزرو اینترنتی بلیط سفر و اقامتگاه'
);

useSeoMeta({
    title: title.value,
    description: landingPageDescription,
    ogTitle: title.value,
    ogDescription: landingPageDescription,
    ogUrl: `https://mrbilit.com${route.path}`,
});

const iOS = ref(false);
const showDownloadApp = ref(false);
const pageTitle = computed(() => {
    if (route.path === '/') {
        return 'مِستربلیط سامانه رزرو اینترنتی بلیط سفر و اقامتگاه';
    } else {
        return contentStore.landingPageH1 || '';
    }
});
const campaignModals = utmCampaigns.value
    .map(campaign => {
        const modal = useModal({
            component: UtmCampaignModal,
            attrs: {
                campaign,
            },
        });
        return { modal, id: campaign.identifier };
    })
    .reduce(
        (previousValue, currentValue) => ({
            ...previousValue,
            [currentValue.id]: currentValue.modal,
        }),
        {} as Record<string, UseModalResult<any, any>>
    );
const showQuickAccess = computed(() =>
    [
        '/',
        '/plane-ticket',
        '/hotel',
        '/tour',
        '/train-ticket',
        '/bus-ticket',
        '/taxi',
    ].includes(route.path)
);

const showHotelBlock = computed(() => route.path.includes('hotel'));

const isForeign = computed(
    () => landingPageContent.value?.mode === SearchMode.Foreign
);

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
    const c = JSON.parse(
        (route.meta.breadcrumbs ?? '[]') as string
    ) as BreadcrumbItem[];
    return c
        .filter(b => !b.foreign || isForeign.value)
        .map(breadcrumb => ({
            ...breadcrumb,
            link: breadcrumb.link.replace('$routePath', route.path),
            title: breadcrumb.title.replace('$cityName', landingCityName.value),
        }));
});

onMounted(async () => {
    iOS.value =
        !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
    if (!searchFormStore.reserveType) {
        searchFormStore.setReserveType(ReserveType.Flight);
    }
    await contentStore.loadUtmCampaigns();
    const utmContent = route.query.utm_content as string | undefined;
    if (utmContent) {
        campaignModals[utmContent]?.open();
    }
    const signupMac = route.query.signUpMac?.toString();
    const mobile = route.query.mobile?.toString();
    if (signupMac && mobile) {
        authStore.signupMagically({ signupMac, mobile });
    }
    if (!isWebApp.value) {
        setTimeout(() => {
            showDownloadApp.value =
                !LocalStorageUtils.hasValidItem('showAppDownload');
        }, 10000);
    }
});
</script>

<template>
    <div class="home-page-container">
        <DelayHydration>
            <TopBanners />
        </DelayHydration>
        <SearchForm :show-quick-access />
        <h1 class="header-title">
            {{ pageTitle }}
        </h1>
        <LandingBanners />
        <HotelBlocking v-if="showHotelBlock" class="blocking" />
        <CityCards />
        <DelayHydration>
            <AdvantageList />
        </DelayHydration>
        <article>
            <nuxt-page />
        </article>
        <DelayHydration>
            <DownloadApp v-if="!isWebApp" />
            <div v-if="['/', '/hotel'].includes(route.path)" id="link-box">
                <BusyWay />
            </div>
            <VehicleLinks v-if="route.path === '/'" />
        </DelayHydration>
        <div class="bread-container">
            <mr-breadcrumb
                v-if="breadcrumbs.length"
                class="crumb"
                :items="breadcrumbs"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.blocking {
    overflow-x: hidden;
}
.home-page-container {
    background-color: white;
}
.bread-container {
    background-color: usePalette($p-cool-gray, $p-100);
    @include mobile-only(1232px) {
        padding-inline: 16px;
    }
    .crumb {
        max-width: 1200px;
        width: 100%;
        margin-inline: auto;
    }
}
.header-title {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    margin: 20px 0 16px 0;
    @include tg-display3($w7, usePalette($p-cool-gray, $p-700));
    @include mobile-only {
        @include tg-xl($w7, usePalette($p-cool-gray, $p-700));
        text-align: center;
    }
}

div#link-box {
    background: usePalette($p-cool-gray, $p-100);
    padding: 16px 0 36px 0;
}

@media (max-width: $app-mobile-max-width) {
    div#link-box {
        padding: 16px 0 21px 0;
    }
}
</style>
